/**
 * Mock fixtures for Phase F1 (site-based dataset selection).
 *
 * Authoritative shape: FederateX_frontend/API_Contracts/2026-05-17_proposed_contracts_v2.md
 * Source fixture:      FederateX_frontend/Mock_Data/2026-05-16_mock_client_sites.json
 *
 * These are mock objects. Frontend never receives PVC paths or filesystem paths
 * from the real backend, including dev-only hint strings. dataset_ref and
 * capability_id are opaque tokens; do not parse them.
 *
 * Real backend endpoints (GET /sites, GET /sites/{id}/capabilities, GET /me/sites)
 * are not implemented yet. These fixtures back the UI through Phase F1.
 */

/**
 * @typedef {"online" | "offline" | "unknown" | "maintenance"} SiteStatus
 * @typedef {"self" | "organization" | "denied"} AccessScope
 * @typedef {"tabular" | "imaging_classification" | "imaging_segmentation"} SupportedTask
 * @typedef {"ready" | "warming_up" | "needs_attention" | "unsupported"} CapabilityReadiness
 * @typedef {"tabular" | "imaging"} DatasetType
 * @typedef {"lidc" | "prostatex"} ImagingDatasetSubtype
 */

/**
 * @typedef {Object} ClientSite
 * @property {string} site_id
 * @property {string} display_name
 * @property {string=} client_app_id
 * @property {SiteStatus} status
 * @property {SupportedTask[]} supported_tasks
 * @property {string=} last_seen
 * @property {string=} organization_id
 * @property {string=} organization_display_name
 * @property {AccessScope} access_scope
 */

/**
 * @typedef {Object} SiteDatasetCapability
 * @property {string} capability_id
 * @property {string} site_id
 * @property {string} dataset_ref
 * @property {string} display_name
 * @property {DatasetType} dataset_type
 * @property {ImagingDatasetSubtype=} imaging_dataset_subtype
 * @property {SupportedTask[]} tasks_supported
 * @property {CapabilityReadiness} readiness_status
 * @property {number | string=} sample_count_estimate
 * @property {string=} notes
 */

/** @type {ClientSite[]} */
export const mockSites = [
  // ── Hospitals (4) ───────────────────────────────────────────────────
  {
    site_id: "site_tgh_01",
    display_name: "Toronto Grand Hospital",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_classification", "imaging_segmentation"],
    last_seen: "2026-05-16T12:14:02Z",
    organization_id: "org_uhn",
    organization_display_name: "Unified Health Network",
    access_scope: "self",
  },
  {
    site_id: "site_smh_01",
    display_name: "South Metro Hospital",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_classification"],
    last_seen: "2026-05-16T12:13:47Z",
    organization_id: "org_unity",
    organization_display_name: "South Metro Health",
    access_scope: "organization",
  },
  {
    site_id: "site_sun_01",
    display_name: "Sunridge Health Sciences Centre",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_segmentation"],
    last_seen: "2026-05-16T12:11:30Z",
    organization_id: "org_sunnybrook",
    organization_display_name: "Sunridge Health",
    access_scope: "organization",
  },
  {
    site_id: "site_mtl_01",
    display_name: "Montréal Metropolitan Hospital",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_classification"],
    last_seen: "2026-05-16T12:10:18Z",
    organization_id: "org_mcgill",
    organization_display_name: "Ville-Marie University Health Centre",
    access_scope: "organization",
  },

  // ── Research institutions (2) ───────────────────────────────────────
  {
    site_id: "site_oicr_01",
    display_name: "Ontario Institute for Clinical Research",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_segmentation"],
    last_seen: "2026-05-16T11:48:30Z",
    organization_id: "org_oicr",
    organization_display_name: "OICR",
    access_scope: "organization",
  },
  {
    site_id: "site_bccrc_01",
    display_name: "Beacon Coast Cancer Research Centre",
    client_app_id: "client_app_pubmed_v3",
    status: "online",
    supported_tasks: ["tabular", "imaging_classification", "imaging_segmentation"],
    last_seen: "2026-05-16T12:02:14Z",
    organization_id: "org_bccrc",
    organization_display_name: "Beacon Coast Cancer",
    access_scope: "self",
  },
];

/**
 * SiteDatasetCapability fixture.
 *
 * Coverage invariants (enforced by hand for the prototype):
 *   1. Each dataset_ref has at least 2 site capabilities so a federated run
 *      can always co-select 2 sites for the same logical dataset.
 *   2. Each task (tabular / imaging_classification / imaging_segmentation)
 *      has at least 2 selectable (online, non-denied) sites.
 *   3. Capability readiness still carries variety (ready / warming_up /
 *      needs_attention) for visual demonstration, but site-level status
 *      is "online" for all 6 sites (the offline Demo site was removed).
 */
/** @type {SiteDatasetCapability[]} */
export const mockCapabilities = [
  // ── Tabular: Primary Site Oncology (5 sites) ─────────────────────────
  {
    capability_id: "cap_sun_tabular_primary",
    site_id: "site_sun_01",
    dataset_ref: "tabular/primary_site_oncology_v1",
    display_name: "Primary Site Oncology",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "11000+",
  },
  {
    capability_id: "cap_tgh_tabular_primary",
    site_id: "site_tgh_01",
    dataset_ref: "tabular/primary_site_oncology_v1",
    display_name: "Primary Site Oncology",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "8500+",
  },
  {
    capability_id: "cap_mtl_tabular_primary",
    site_id: "site_mtl_01",
    dataset_ref: "tabular/primary_site_oncology_v1",
    display_name: "Primary Site Oncology",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "6200+",
  },
  {
    capability_id: "cap_oicr_tabular_primary",
    site_id: "site_oicr_01",
    dataset_ref: "tabular/primary_site_oncology_v1",
    display_name: "Primary Site Oncology",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "needs_attention",
    sample_count_estimate: "1800+",
    notes: "Schema check pending, see site admin",
  },
  {
    capability_id: "cap_bccrc_tabular_primary",
    site_id: "site_bccrc_01",
    dataset_ref: "tabular/primary_site_oncology_v1",
    display_name: "Primary Site Oncology",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "5500+",
  },

  // ── Tabular: OMOP Tumour Cell Range (3 sites) ────────────────────────
  {
    capability_id: "cap_sun_tabular_omop",
    site_id: "site_sun_01",
    dataset_ref: "tabular/omop_tumor_cell_range_v1",
    display_name: "OMOP Tumour Cell Range",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "1200+",
  },
  {
    capability_id: "cap_tgh_tabular_omop",
    site_id: "site_tgh_01",
    dataset_ref: "tabular/omop_tumor_cell_range_v1",
    display_name: "OMOP Tumour Cell Range",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "800+",
  },
  {
    capability_id: "cap_smh_tabular_omop",
    site_id: "site_smh_01",
    dataset_ref: "tabular/omop_tumor_cell_range_v1",
    display_name: "OMOP Tumour Cell Range",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "600+",
  },

  // ── Tabular: BRCA Survival (2 sites) ─────────────────────────────────
  {
    capability_id: "cap_sun_tabular_brca",
    site_id: "site_sun_01",
    dataset_ref: "tabular/brca_survival_v1",
    display_name: "BRCA Survival",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "1000+",
  },
  {
    capability_id: "cap_oicr_tabular_brca",
    site_id: "site_oicr_01",
    dataset_ref: "tabular/brca_survival_v1",
    display_name: "BRCA Survival",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "850+",
  },

  // ── Tabular: Therapy Type Classification (4 sites) ───────────────────
  {
    capability_id: "cap_sun_tabular_therapy",
    site_id: "site_sun_01",
    dataset_ref: "tabular/therapy_type_v1",
    display_name: "Therapy Type Classification",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "1100+",
  },
  {
    capability_id: "cap_tgh_tabular_therapy",
    site_id: "site_tgh_01",
    dataset_ref: "tabular/therapy_type_v1",
    display_name: "Therapy Type Classification",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "800+",
  },
  {
    capability_id: "cap_mtl_tabular_therapy",
    site_id: "site_mtl_01",
    dataset_ref: "tabular/therapy_type_v1",
    display_name: "Therapy Type Classification",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "warming_up",
    sample_count_estimate: "500+",
    notes: "Cache sync in progress",
  },
  {
    capability_id: "cap_bccrc_tabular_therapy",
    site_id: "site_bccrc_01",
    dataset_ref: "tabular/therapy_type_v1",
    display_name: "Therapy Type Classification",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "700+",
  },

  // ── Tabular: Treatment Outcome (3 sites) ─────────────────────────────
  {
    capability_id: "cap_tgh_tabular_treatment",
    site_id: "site_tgh_01",
    dataset_ref: "tabular/treatment_outcome_v1",
    display_name: "Treatment Outcome",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "1500+",
  },
  {
    capability_id: "cap_smh_tabular_treatment",
    site_id: "site_smh_01",
    dataset_ref: "tabular/treatment_outcome_v1",
    display_name: "Treatment Outcome",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "ready",
    sample_count_estimate: "1200+",
  },
  {
    capability_id: "cap_oicr_tabular_treatment",
    site_id: "site_oicr_01",
    dataset_ref: "tabular/treatment_outcome_v1",
    display_name: "Treatment Outcome",
    dataset_type: "tabular",
    tasks_supported: ["tabular"],
    readiness_status: "needs_attention",
    sample_count_estimate: "900+",
    notes: "Label coverage below threshold",
  },

  // ── Imaging Classification: ProstateX Cls (2 sites) ──────────────────
  {
    capability_id: "cap_tgh_prostatex_cls",
    site_id: "site_tgh_01",
    dataset_ref: "prostatex_cls_v1",
    display_name: "ProstateX (classification cohort)",
    dataset_type: "imaging",
    imaging_dataset_subtype: "prostatex",
    tasks_supported: ["imaging_classification"],
    readiness_status: "ready",
    sample_count_estimate: "150+",
  },
  {
    capability_id: "cap_mtl_prostatex_cls",
    site_id: "site_mtl_01",
    dataset_ref: "prostatex_cls_v1",
    display_name: "ProstateX (classification cohort)",
    dataset_type: "imaging",
    imaging_dataset_subtype: "prostatex",
    tasks_supported: ["imaging_classification"],
    readiness_status: "ready",
    sample_count_estimate: "120+",
  },

  // ── Imaging Classification: LIDC Cls (3 sites) ───────────────────────
  {
    capability_id: "cap_smh_lidc_cls",
    site_id: "site_smh_01",
    dataset_ref: "lidc_cls_v1",
    display_name: "LIDC-IDRI Lung Nodule Patches",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_classification"],
    readiness_status: "ready",
    sample_count_estimate: "800+ nodules",
  },
  {
    capability_id: "cap_mtl_lidc_cls",
    site_id: "site_mtl_01",
    dataset_ref: "lidc_cls_v1",
    display_name: "LIDC-IDRI Lung Nodule Patches",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_classification"],
    readiness_status: "ready",
    sample_count_estimate: "650+ nodules",
  },
  {
    capability_id: "cap_bccrc_lidc_cls",
    site_id: "site_bccrc_01",
    dataset_ref: "lidc_cls_v1",
    display_name: "LIDC-IDRI Lung Nodule Patches",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_classification"],
    readiness_status: "ready",
    sample_count_estimate: "900+ nodules",
  },

  // ── Imaging Segmentation: ProstateX Seg (3 sites) ────────────────────
  {
    capability_id: "cap_tgh_prostatex_seg",
    site_id: "site_tgh_01",
    dataset_ref: "prostatex_seg_v1",
    display_name: "ProstateX (segmentation cohort)",
    dataset_type: "imaging",
    imaging_dataset_subtype: "prostatex",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "ready",
    sample_count_estimate: "200+",
  },
  {
    capability_id: "cap_oicr_prostatex_seg",
    site_id: "site_oicr_01",
    dataset_ref: "prostatex_seg_v1",
    display_name: "ProstateX (segmentation cohort)",
    dataset_type: "imaging",
    imaging_dataset_subtype: "prostatex",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "ready",
    sample_count_estimate: "180+",
  },
  {
    capability_id: "cap_bccrc_prostatex_seg",
    site_id: "site_bccrc_01",
    dataset_ref: "prostatex_seg_v1",
    display_name: "ProstateX (segmentation cohort)",
    dataset_type: "imaging",
    imaging_dataset_subtype: "prostatex",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "ready",
    sample_count_estimate: "220+",
  },

  // ── Imaging Segmentation: LIDC Seg (3 sites) ─────────────────────────
  {
    capability_id: "cap_sun_lidc_seg",
    site_id: "site_sun_01",
    dataset_ref: "lidc_seg_v1",
    display_name: "LIDC-IDRI Volumetric Segmentation",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "warming_up",
    sample_count_estimate: "1000+",
  },
  {
    capability_id: "cap_oicr_lidc_seg",
    site_id: "site_oicr_01",
    dataset_ref: "lidc_seg_v1",
    display_name: "LIDC-IDRI Volumetric Segmentation",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "ready",
    sample_count_estimate: "900+",
  },
  {
    capability_id: "cap_bccrc_lidc_seg",
    site_id: "site_bccrc_01",
    dataset_ref: "lidc_seg_v1",
    display_name: "LIDC-IDRI Volumetric Segmentation",
    dataset_type: "imaging",
    imaging_dataset_subtype: "lidc",
    tasks_supported: ["imaging_segmentation"],
    readiness_status: "ready",
    sample_count_estimate: "1100+",
  },
];

/**
 * Resolve capabilities for a given site_id from the mock fixture.
 * @param {string} siteId
 * @returns {SiteDatasetCapability[]}
 */
export const mockCapabilitiesForSite = (siteId) =>
  mockCapabilities.filter((cap) => cap.site_id === siteId);

/**
 * Whether a site can be selected for a federated run. Offline / maintenance /
 * denied sites still render in the list (visually distinct) but are excluded
 * from selection.
 * @param {ClientSite} site
 * @returns {boolean}
 */
export const isSiteSelectable = (site) =>
  site.status === "online" && site.access_scope !== "denied";

/**
 * Group SiteDatasetCapability[] by `dataset_ref` for the Option C
 * /datasets registry view. Capabilities sharing a dataset_ref are the
 * same logical dataset offered by different sites.
 *
 * Pure function. Frontend never trusts this for ACL — it's purely a
 * presentation grouping over already-fetched capabilities.
 *
 * Returns:
 *   {
 *     dataset_ref,                     // group key (opaque slug from backend)
 *     display_name,                    // first non-null display_name in group, or dataset_ref
 *     dataset_type,                    // "tabular" | "imaging" (first-seen)
 *     imaging_dataset_subtype,         // first-seen (or undefined for tabular)
 *     tasks_supported: string[],       // union of group's tasks_supported, sorted
 *     capabilities: SiteDatasetCapability[],
 *                                      // sorted by owning site display_name when `sites` is provided,
 *                                      // else by site_id
 *     sites_count,                     // capabilities.length
 *     readiness_counts: { [readiness_status]: number },
 *     all_ready: boolean,              // shortcut for "every capability has readiness ready"
 *   }
 *
 * @param {SiteDatasetCapability[]} capabilities
 * @param {ClientSite[]=} sites optional, improves the per-site sort order
 */
export const groupCapabilitiesByDatasetRef = (capabilities, sites) => {
  const safeCaps = Array.isArray(capabilities) ? capabilities : [];
  const safeSites = Array.isArray(sites) ? sites : [];

  const siteDisplayName = (siteId) => {
    const s = safeSites.find((site) => site.site_id === siteId);
    return s?.display_name || siteId || "";
  };

  /** @type {Record<string, any>} */
  const byRef = {};
  for (const cap of safeCaps) {
    if (!cap || !cap.dataset_ref) continue;
    const key = cap.dataset_ref;
    if (!byRef[key]) {
      byRef[key] = {
        dataset_ref: key,
        display_name: cap.display_name || key,
        dataset_type: cap.dataset_type,
        imaging_dataset_subtype: cap.imaging_dataset_subtype,
        _tasks: new Set(),
        capabilities: [],
      };
    }
    (cap.tasks_supported || []).forEach((t) => byRef[key]._tasks.add(t));
    byRef[key].capabilities.push(cap);
  }

  return Object.values(byRef).map((g) => {
    const sortedCaps = [...g.capabilities].sort((a, b) =>
      siteDisplayName(a.site_id).localeCompare(siteDisplayName(b.site_id))
    );
    const readinessCounts = sortedCaps.reduce((acc, c) => {
      const k = c.readiness_status || "unknown";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, /** @type {Record<string, number>} */ ({}));

    return {
      dataset_ref: g.dataset_ref,
      display_name: g.display_name,
      dataset_type: g.dataset_type,
      imaging_dataset_subtype: g.imaging_dataset_subtype,
      tasks_supported: [...g._tasks].sort(),
      capabilities: sortedCaps,
      sites_count: sortedCaps.length,
      readiness_counts: readinessCounts,
      all_ready: sortedCaps.every((c) => c.readiness_status === "ready"),
    };
  });
};
